import { initContract } from "@ts-rest/core";
import { z } from "zod";
import { FacilityNodeSchema } from "./facility.contract.js";
/**
 * The shop-floor kiosk, legacy "Generischer User" (ADR-036). A KIOSK PROFILE names the facilities a shared device
 * offers, grouped into departments — replacing the legacy's 365-day `genericUserData` cookie and its four hard-coded
 * customer presets with a server-side record.
 *
 * **A profile is a NAVIGATION scope, never a permission** (ADR-036 D5): what the device may actually read remains the
 * `GenericUser` role's facility grants, enforced by the same authz engine as every other caller. `getMyKiosk`
 * therefore intersects the profile with what the caller can really view.
 */
const c = initContract();
const UnauthorizedSchema = z.object({ message: z.string() });
/** Managing profiles is SuperAdmin-only, as the legacy sidebar entry was. */
const KioskForbiddenSchema = z.object({ message: z.string(), code: z.literal("KIOSK_FORBIDDEN") });
const KioskNotFoundSchema = z.object({ message: z.string(), code: z.literal("KIOSK_PROFILE_NOT_FOUND") });
export const KioskDepartmentSchema = z.object({
    id: z.number().int(),
    name: z.string(),
    /** Ordered; may be empty while the admin is still building the profile. */
    facilityIds: z.array(z.number().int().positive()),
});
export const KioskProfileSchema = z.object({
    id: z.number().int(),
    name: z.string(),
    departments: z.array(KioskDepartmentSchema),
    createdAt: z.string(), // ISO-8601 UTC
    /** The concurrency token every PATCH sends back (opaque to the client). */
    updatedAt: z.string(),
});
/** A department as the KIOSK sees it: the facilities resolved to tree nodes, already intersected with what the
 *  caller may view. A department whose facilities are all invisible is dropped, never rendered empty. */
export const KioskResolvedDepartmentSchema = z.object({
    id: z.number().int(),
    name: z.string(),
    facilities: z.array(FacilityNodeSchema),
});
export const KioskResolvedProfileSchema = z.object({
    id: z.number().int(),
    name: z.string(),
    departments: z.array(KioskResolvedDepartmentSchema),
});
/**
 * The kiosk's own read. It NEVER fails in a way that needs someone to walk to the device (plan panel): when the
 * requested profile is gone, or none was requested and the realm has several, it answers with `profile: null` plus
 * the pickable `available` list so the screen can re-point itself.
 */
export const KioskMeSchema = z.object({
    profile: KioskResolvedProfileSchema.nullable(),
    /** Every live profile in the realm — the device's self-service picker. */
    available: z.array(z.object({ id: z.number().int(), name: z.string() })),
    /** The `profileId` asked for no longer exists: the device clears its stored id and says so. */
    requestedProfileMissing: z.boolean(),
});
const DepartmentInputSchema = z.object({
    name: z.string().trim().min(1).max(120),
    facilityIds: z.array(z.number().int().positive()).max(200),
});
export const KioskProfileBodySchema = z.object({
    name: z.string().trim().min(1).max(120),
    departments: z.array(DepartmentInputSchema).max(20),
});
export const kioskContract = c.router({
    listProfiles: {
        method: "GET",
        path: "/kiosk/profiles",
        responses: { 200: z.object({ profiles: z.array(KioskProfileSchema) }), 401: UnauthorizedSchema, 403: KioskForbiddenSchema },
        summary: "The realm's kiosk profiles (SuperAdmin only) — the legacy Generic User setup screen.",
    },
    createProfile: {
        method: "POST",
        path: "/kiosk/profiles",
        body: KioskProfileBodySchema,
        responses: {
            200: KioskProfileSchema,
            400: z.object({ message: z.string().optional() }).passthrough(),
            401: UnauthorizedSchema,
            403: KioskForbiddenSchema,
            409: z.object({ message: z.string(), code: z.literal("KIOSK_NAME_TAKEN") }),
        },
        summary: "Create a kiosk profile (SuperAdmin only).",
    },
    updateProfile: {
        method: "PATCH",
        path: "/kiosk/profiles/:id",
        pathParams: z.object({ id: z.coerce.number().int().positive().max(2147483647) }),
        /** `expectedUpdatedAt` is the profile's token — a concurrent edit is a 409, never a silent overwrite. */
        body: KioskProfileBodySchema.extend({ expectedUpdatedAt: z.string().min(1) }),
        responses: {
            200: KioskProfileSchema,
            400: z.object({ message: z.string().optional() }).passthrough(),
            401: UnauthorizedSchema,
            403: KioskForbiddenSchema,
            404: KioskNotFoundSchema,
            409: z.object({ message: z.string(), code: z.enum(["KIOSK_PROFILE_STALE", "KIOSK_NAME_TAKEN"]) }),
        },
        summary: "Replace a kiosk profile's name + departments (SuperAdmin only; optimistic-concurrency token).",
    },
    deleteProfile: {
        method: "DELETE",
        path: "/kiosk/profiles/:id",
        pathParams: z.object({ id: z.coerce.number().int().positive().max(2147483647) }),
        body: z.object({}).optional(),
        responses: { 200: z.object({ deleted: z.literal(true) }), 401: UnauthorizedSchema, 403: KioskForbiddenSchema, 404: KioskNotFoundSchema },
        summary: "Soft-delete a kiosk profile (SuperAdmin only). Devices using it fall back to the picker, never a dead screen.",
    },
    getMyKiosk: {
        method: "GET",
        path: "/kiosk/me",
        query: z.object({ profileId: z.coerce.number().int().positive().max(2147483647).optional() }),
        responses: { 200: KioskMeSchema, 401: UnauthorizedSchema },
        summary: "The kiosk's own view: its profile resolved + intersected with what the caller may view, with a self-service picker fallback (ADR-036).",
    },
});
//# sourceMappingURL=kiosk.contract.js.map